import styled from 'styled-components';
import { jd, JD_FONT } from 'app/pages/flat/DashboardV2/tokens';

export const Icon = styled.div<{ $active?: boolean }>`
  padding: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 24px;
  border-radius: 4px;
  transition: background 0.22s ease;
  ${({ $active }) => $active && `background: ${jd.mint};`}

  &:hover {
    background: ${({ $active }) => ($active ? jd.mint : 'rgba(0, 0, 0, 0.04)')};
  }
`;

export const IconWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  cursor: pointer;

  &:hover ${Icon} {
    background: rgba(0, 0, 0, 0.04);
  }
`;

export const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

export const Panel = styled.div<{ $left?: number; $bottom?: number }>`
  position: fixed;
  left: ${({ $left = 0 }) => $left}px;
  bottom: ${({ $bottom = 0 }) => $bottom}px;
  width: 292px;
  padding: 6px;
  border: 1px solid ${jd.border};
  border-radius: 16px;
  background: ${jd.surface};
  box-shadow: ${jd.overlayShadow};
  font-family: ${JD_FONT};
  z-index: 1300;
  overflow: hidden;

  @media (prefers-reduced-motion: no-preference) {
    animation: profile-menu-open 0.18s ease both;
  }

  @keyframes profile-menu-open {
    from {
      opacity: 0;
      transform: translateY(-6px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const Top = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px 12px;
  border-bottom: 1px solid ${jd.border};
`;

export const Avatar = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  /* Verde sólido da Jusfy — sem degradê. Só aparece por trás das iniciais;
     com foto, a imagem cobre o círculo inteiro (ver AvatarImage). */
  background: ${jd.primary};
  font-size: 12.5px;
  font-weight: 700;
  color: ${jd.primaryContrast};
`;

export const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

export const Who = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

export const Name = styled.span`
  overflow: hidden;
  font-size: 13.5px;
  font-weight: 600;
  color: ${jd.text};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Email = styled.span`
  overflow: hidden;
  font-size: 11.5px;
  color: ${jd.text2};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const EditButton = styled.button`
  flex-shrink: 0;
  margin-left: auto;
  padding: 5px 10px;
  border: 1px solid ${jd.mintBorder};
  border-radius: 7px;
  background: ${jd.mintFaint};
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${jd.primaryStrong};
  cursor: pointer;
  transition: border-color 0.18s ease, background 0.18s ease;

  &:hover {
    border-color: ${jd.primary};
    background: ${jd.mint};
  }

  &:focus-visible {
    outline: 2px solid ${jd.primary};
    outline-offset: 2px;
  }
`;

export const Group = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0 6px;
  border-bottom: 1px solid ${jd.border};
`;

export const GroupLabel = styled.span`
  padding: 4px 10px 8px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${jd.text3};
`;

export const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 10px;
  border: none;
  border-radius: 9px;
  background: transparent;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 500;
  color: ${jd.text};
  text-align: left;
  cursor: pointer;
  transition: background 0.16s ease, color 0.16s ease;

  svg {
    flex-shrink: 0;
    color: ${jd.text3};
    transition: color 0.16s ease;
  }

  &:hover:not(:disabled) {
    background: ${jd.mintFaint};
    color: ${jd.primaryStrong};
  }

  &:hover:not(:disabled) svg {
    color: ${jd.primary};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:focus-visible {
    outline: 2px solid ${jd.primary};
    outline-offset: 2px;
  }
`;

export const InviteBanner = styled.button`
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  margin: 6px 0;
  padding: 10px;
  border: none;
  border-radius: 10px;
  background: ${jd.mint};
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: ${jd.primaryStrong};
  text-align: left;
  cursor: pointer;
  transition: background 0.18s ease;

  &:hover {
    background: ${jd.mintStrong};
  }

  &:focus-visible {
    outline: 2px solid ${jd.primary};
    outline-offset: 2px;
  }
`;

export const InviteIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  color: ${jd.gold};
`;

/*
 * Sem o deslize de 3px da referência: no popup o
 * movimento lia como tremida/bug (feedback de 2026-08-20). O hover fica só
 * na troca de fundo do banner.
 */
export const InviteArrow = styled.span`
  display: flex;
  margin-left: auto;
  flex-shrink: 0;
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 4px 2px;
  border-top: 1px solid ${jd.border};
`;

export const HelpButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: ${jd.text2};
  cursor: pointer;
  transition: background 0.16s ease, color 0.16s ease;

  svg {
    flex-shrink: 0;
  }

  &:hover {
    background: ${jd.mintFaint};
    color: ${jd.primaryStrong};
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  &:focus-visible {
    outline: 2px solid ${jd.primary};
    outline-offset: 2px;
  }
`;

export const LogoutButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 10px;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: ${jd.text2};
  cursor: pointer;
  transition: background 0.16s ease, color 0.16s ease;

  &:hover {
    background: ${jd.dangerSoft};
    color: ${jd.danger};
  }

  &:focus-visible {
    outline: 2px solid ${jd.primary};
    outline-offset: 2px;
  }
`;
